import { AspectRatio, ChannelPlatformType, StreamingProtocol } from "./enums";
import moment from "moment";
import { ChannelPreview } from "./channel-preview";
import { TimeSpotRule } from "./timespot";
import { Resolution, ResolutionPreset } from "./channel-codec";
import { EdgeServer } from "./edge-server";

export class Channel {
  platforms = [];

  constructor(
    public id: number,
    public uid: string,
    public description: string,
    public name: string,
    public placeholderImage: string,
    public isActive: boolean,
    public createdDate: moment.Moment,
    public timezoneId: number,
    public framerate: number,
    public isMuted: boolean = true,
    public isTemplate: boolean = false,
    public previewUrls: any,
    public _isLiveLocked: boolean,
    public _availabilityZoneIds: number[],
    public isBlock: boolean,
    public isBlockStack: boolean,
  ) {}
  public get isLiveLocked(): boolean {
    return this._isLiveLocked;
  }
  public set isLiveLocked(val: boolean) {
    console.log("live.component->Channel->isLiveLocked set to ", val);
    this._isLiveLocked = val;
  }
}

export class ChannelInfo extends Channel {
  constructor(
    id: number,
    uid: string,
    description: string,
    name: string,
    public packshotUrl: string,
    isActive: boolean,
    createdDate: moment.Moment,
    public placeholderImage,
    public timezoneId: number,
    public burnSubtitles: boolean,
    public subtitleLanguage: string,
    public subtitleFontSettings: {
      fontId: number;
      size: number;
      subtitleStartPositionFromTop: number;
      subtitleEndPositionFromTop: number;
    },
    framerate: number,
    public platforms: ChannelPlatform[],
    public aspectRatio: AspectRatio,
    public availabilityZones: AvailabilityZoneSettings[],
    previews: ChannelPreview[],
    public isTemplate: boolean,
    public templateCategories: number[],
    public templateTrailerUrl: string,
    public logoX: number,
    public logoY: number,
    public logoHeight: number,
    public logoWidth: number,
    public logoZIndex: number,
    public workingHours: WorkingHoursSettings,
    public mainLanguageCode: string,
    public timeSpotRules: Array<TimeSpotRule>,
    public isRecordingActive: boolean,
    public playOneItemPerDepth: boolean,
    public playbackOffset: number,
    public _isLiveLocked: boolean,
    public schedulerDaysToLoad: number,
    public isBlock: boolean,
    public isBlockStack: boolean,
    public uploadPlaylistCsvSettings: UploadPlaylistCsvSettings,
    public isCreatedReady: boolean,
    public audioNormalization: number = null,
    public useLUFS: boolean = null,
    public signalCommands: ChannelSignalCommandSetting[] = [],
    public scteOutCommandId: number = null,
    public scteInCommandId: number = null,
    public scteTagIds: number[] = [],
    public webRtcPreviewEdgeServerId?: number,
    public hlsPreviewEdgeServerId?: number,
    public futurePreviewEdgeServerId?: number,
    public liveBroadcastPresetShowUid?: string,
    public autoExtendLiveShowBehaviour: EAutoExtendLiveShowBehaviour = EAutoExtendLiveShowBehaviour.Empty,
    public audioLangSettings: AudioLangSettings[] = [],
    public closedCaptions: ClosedCaptionSetting[] = [],
  ) {
    super(
      id,
      uid,
      description,
      name,
      placeholderImage,
      isActive,
      createdDate,
      timezoneId,
      framerate,
      true,
      isTemplate,
      previews,
      _isLiveLocked ||
        autoExtendLiveShowBehaviour ===
          EAutoExtendLiveShowBehaviour.EndlesslyExtendLiveShow,
      availabilityZones.map((z) => z.id),
      isBlock,
      isBlockStack,
    );
  }
  public get aspectRationNumber(): number {
    switch (this.aspectRatio) {
      case AspectRatio.SixteenByNine:
        return 16 / 9;
      case AspectRatio.FourByThree:
        return 4 / 3;
      case AspectRatio.OneByOne:
        return 1;
    }
  }
  public get availabilityZoneIds(): number[] {
    return this.availabilityZones.map((x) => x.id);
  }
  public set availabilityZoneIds(newVal: number[]) {
    this.availabilityZones = newVal.map((availabilityZoneId) => {
      let overrideChannelJson = null;
      const existingAvailabilityZone = this.availabilityZones.find(
        (x) => x.id === availabilityZoneId,
      );
      if (
        !!existingAvailabilityZone &&
        existingAvailabilityZone.overrideChannelJson
      ) {
        overrideChannelJson = existingAvailabilityZone.overrideChannelJson;
      }
      return new AvailabilityZoneSettings(
        availabilityZoneId,
        overrideChannelJson,
      );
    });
  }

  public get overrideChannelJsonAvailabilityZoneIds(): number[] {
    return this.availabilityZones
      .filter((x) => !!x.overrideChannelJson)
      .map((x) => x.id);
  }
}
export class AvailabilityZoneSettings {
  constructor(
    public id: number,
    public overrideChannelJson: string,
  ) {}
}

export class AudioLangSettings {
  constructor(
    public languageCode: string,
    public skipOriginalMediaAudioLanguage: boolean,
    public audioFallbackLanguages: string[],
  ) {}
}

export enum EClosedCaptionsTransport {
  Undefined = 0,
  VBI = 1,
  DTVCC = 2,
}

export enum EClosedCaptionsType {
  Undefined = 0,
  Line21CC1 = 1,
  Line21CC2 = 2,
  Line21CC3 = 3,
  Line21CC4 = 4,
  Line21T1 = 5,
  Line21T2 = 6,
  Line21T3 = 7,
  Line21T4 = 8,
  Line21XDS = 9,
  DTVCC = 10,
}

export class ClosedCaptionSetting {
  constructor(
    public languageCode: string,
    public captionsTransport: EClosedCaptionsTransport,
    public captionsType: EClosedCaptionsType,
    public captionsServiceNumber: number,
  ) {}
}

export class ChannelSignalCommandSetting {
  constructor(
    public id: number,
    public commandName: string,
    public standartType: SecondaryEventSignalStandardType,
  ) {}
}

export enum SecondaryEventSignalStandardType {
  DTMF = 1,
  SCTE35 = 2,
  SCTE104 = 3,
  CuePeriod = 4,
}
export class UploadPlaylistCsvSettings {
  constructor(
    public mediaIdPrefixesToAlert: Array<string>,
    public allowedFileNameWildcard: string,
    public allowedHoursFromNow: number = null,
  ) {}
  public static DEFAULT = new UploadPlaylistCsvSettings([], null, null);
}
export abstract class ChannelPlatform {
  constructor(
    public platformId: number,
    public isActive: boolean,
    public displayLabel: string,
    public channelPlatformType: ChannelPlatformType,
    public edgeServerIds: number[],
  ) {}
}

export class FacebookChannelPlatform extends ChannelPlatform {
  constructor(
    public platformId: number,
    isActive: boolean,
  ) {
    super(platformId, isActive, "Facebook", ChannelPlatformType.Facebook, []);
  }
}

export class YoutubeChannelPlatform extends ChannelPlatform {
  constructor(
    public platformId: number,
    isActive: boolean,
  ) {
    super(platformId, isActive, "Youtube", ChannelPlatformType.Youtube, []);
  }
}

export class MyWebsiteChannelPlatform extends ChannelPlatform {
  constructor(
    public platformId: number,
    isActive: boolean,
  ) {
    super(platformId, isActive, "MyWebsite", ChannelPlatformType.MyWebsite, []);
  }
}

export class MyAppChannelPlatform extends ChannelPlatform {
  constructor(
    public platformId: number,
    isActive: boolean,
  ) {
    super(platformId, isActive, "MyApp", ChannelPlatformType.MyApp, []);
  }
}

export class CustomChannelPlatform extends ChannelPlatform {
  constructor(
    public platformId: number,
    isActive: boolean,
    displayLabel,
    public streamingProtocol: StreamingProtocol,
    public hlsSettings: {
      resolution: number;
      fps: number;
    },
    public pushSettings: ChannelPushSettings,
    public audioLanguages: string[],
    public dvbSubtitles: {
      [languageCode: string]: {
        fontId: number;
        size: number;
        subtitleStartPositionFromTop: number;
        subtitleEndPositionFromTop: number;
      };
    },
    public edgeServerIds: number[],
  ) {
    super(
      platformId,
      isActive,
      displayLabel,
      ChannelPlatformType.Custom,
      edgeServerIds,
    );
  }
}
export abstract class ChannelPushSettings {
  constructor(
    public format: string,
    public codecId: number,
  ) {}
  url?: string;
  pushMode?: boolean;
  pushAddress?: string;
  portNum?: number;
  passPhrase?: string;
  latencyMs?: number;
}
export class ChannelPushMpgtsSettings extends ChannelPushSettings {
  constructor(
    public codecId: number,
    public url: string,
  ) {
    super("mpegts", codecId);
  }
  public static DEFAULT() {
    return new ChannelPushMpgtsSettings(null, "");
  }
}

export class ChannelPushFlvSettings extends ChannelPushSettings {
  constructor(
    public codecId: number,
    public url: string,
  ) {
    super("flv", codecId);
  }
  public static DEFAULT() {
    return new ChannelPushMpgtsSettings(null, "");
  }
}

export class ChannelPushSrtSettings extends ChannelPushSettings {
  constructor(
    public codecId: number,
    public pushMode: boolean,
    public portNum: number,
    public pushAddress: string,
    public passPhrase: string,
    public latencyMs: number,
  ) {
    super("SRT", codecId);
  }
}
export class ChannelDistribution {
  formats: ChannelFormat[] = [];

  addFormat(format: ChannelFormat) {
    this.formats.push(format);
  }

  getFormat(name: string): ChannelFormat {
    return this.formats.find((format: ChannelFormat) => {
      return format.name === name;
    });
  }

  getFormatVideo(name: string) {
    if (!this.formats[name]) {
      return null;
    }

    return this.formats[name].video;
  }
}

export class DefaultPushCodecSettings {
  constructor(
    public videoCodecName: string,
    public videoCodecType: number,
    public videoCodecResolution: ResolutionPreset,
    public videoCodecFPS: number,
    public videoCodecBitrate: number,
    public videoCodecProfile: { name: string; id: number },
    public videoCodecGOPSize: number,
    public videoCodecProgressive: { name: string; id: number },
    public videoCodecMUX: number,
    public videoCodecSpecialOptions: string,
    public audioCodecName: string,
    public audioCodecType: number,
    public audioCodecChannels: { name: string; channels: number },
    public audioCodecSampleRate: number,
    public audioCodecBitrate: number,
    public audioCodecSpecialOptions: string,
    public colourSpace: number,
    public mpegColourRange: boolean,
  ) {}
}

export class VideoCodec {
  constructor(
    public name: string,
    public value: number,
    public fps: number[],
    public profiles: VideoFormatProfile[],
    public progressives: VideoFormatProgressive[],
    public resolutions: ResolutionPreset[],
  ) {}
}

export class ChannelFormat {
  constructor(
    public name: string,
    public displayName: string,
    public exampleUrl: string,
    public audio: AudioCodec[],
    public video: VideoCodec[],
    public defaultSettings: DefaultPushCodecSettings,
  ) {}
}

export class ChannelPushSrtFormat extends ChannelFormat {
  constructor(
    public name: string,
    public displayName: string,
    public exampleUrl: string,
    public audio: AudioCodec[],
    public video: VideoCodec[],
    public defaultSettings: DefaultPushCodecSettings,
    public srtPushMode: boolean,
    public srtPortNum: number,
    public srtPushAddress: string,
    public srtPassPhrase: string,
    public srtLatencyMs: number,
  ) {
    super(name, displayName, exampleUrl, audio, video, defaultSettings);
  }
}

/* Audio Format Elements */
export class AudioCodec {
  constructor(
    public name: string,
    public value: number,
    public bitRate: number[],
    public channels: AudioChannel[],
    public sampleRate: number[],
  ) {}
}

export class AudioChannel {
  constructor(
    public name: string,
    public channels: number,
  ) {}
}

/* Video Format Elements */
export class VideoFormatProfile {
  constructor(
    public name: string,
    public id: number,
  ) {}
}

export class VideoFormatProgressive {
  constructor(
    public name: string,
    public id: number,
  ) {}
}

export class WorkingHoursSettings {
  constructor(
    public startDayOfWeek: number,
    public endDayOfWeek: number,
    public startTimeInMinutes: number,
    public endTimeInMinutes: number,
  ) {}
}

export enum EAutoExtendLiveShowBehaviour {
  EndlesslyExtendLiveShow = 0,
  Empty = 1,
}
